import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.3 Tier 2 — sibling to eva_snapshots_controller. Handles the
// Compare-picker modal and Delete action on the Cash Flow forecasts
// list (both the standalone /cash_flow_forecasts index and the modal
// on the Cash Flow page). Capture lives on cash_flow_controller.js
// (page header button → POST /cash_flow_forecasts).
export default class extends Controller {
  static targets = [
    "compareModal", "compareLeftLabel", "compareRight"
  ]

  static values = {
    projectId: Number,
    budgetId: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._leftForecastId = null
  }

  showCompareModal(event) {
    const { forecastIdParam, forecastLabelParam } = event.currentTarget.dataset
    this._leftForecastId = parseInt(forecastIdParam, 10)
    if (this.hasCompareLeftLabelTarget) {
      this.compareLeftLabelTarget.textContent = forecastLabelParam || `Forecast #${this._leftForecastId}`
    }
    this._show(this.compareModalTarget)
  }

  hideCompareModal() { this._hide(this.compareModalTarget) }

  runCompare() {
    if (!this._leftForecastId) return
    const right = this.compareRightTarget.value
    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/cash_flow_forecasts/compare?left=${this._leftForecastId}&right=${encodeURIComponent(right)}`
    this._track("Cash Flow Forecast Compared", { left: this._leftForecastId, right })
    window.location.href = url
  }

  async deleteForecast(event) {
    const { forecastIdParam, forecastLabelParam } = event.currentTarget.dataset
    const id = parseInt(forecastIdParam, 10)
    const label = forecastLabelParam || `Forecast #${id}`
    const ok = await this._inlineConfirm(`Delete ${label}? This cannot be undone.`)
    if (!ok) return

    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/cash_flow_forecasts/${id}`
    const resp = await fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    })
    if (!resp.ok) {
      this._showToast("Failed to delete forecast", "error")
      return
    }
    this._track("Cash Flow Forecast Deleted", { id })
    const row = this.element.querySelector(`tr[data-forecast-id='${id}']`)
    if (row) row.remove()
    this._showToast("Forecast deleted")
  }

  stopPropagation(event) { event.stopPropagation() }

  _show(el) { if (el) el.classList.remove("hidden") }
  _hide(el) { if (el) el.classList.add("hidden") }

  async _inlineConfirm(message) {
    return new Promise(resolve => {
      const backdrop = document.createElement("div")
      backdrop.className = "fixed inset-0 z-[70] bg-black/40 flex items-center justify-center"
      backdrop.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4" data-inline-panel>
          <div class="px-4 py-3 border-b border-gray-200">
            <h5 class="text-base font-semibold text-gray-800">Delete forecast?</h5>
          </div>
          <div class="px-4 py-3 text-[13px] text-gray-700" data-msg></div>
          <div class="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
            <button type="button" data-cancel class="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-[13px] cursor-pointer">Cancel</button>
            <button type="button" data-confirm class="px-3 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 text-[13px] font-medium cursor-pointer">Delete</button>
          </div>
        </div>
      `
      backdrop.querySelector("[data-msg]").textContent = message
      document.body.appendChild(backdrop)
      const cleanup = result => {
        document.body.removeChild(backdrop)
        resolve(result)
      }
      backdrop.querySelector("[data-cancel]").addEventListener("click", () => cleanup(false))
      backdrop.querySelector("[data-confirm]").addEventListener("click", () => cleanup(true))
      backdrop.addEventListener("click", e => { if (e.target === backdrop) cleanup(false) })
      const panel = backdrop.querySelector("[data-inline-panel]")
      panel.addEventListener("click", e => e.stopPropagation())
    })
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }
}
